import DsDynoChart from "./DsDynoChart";
import DsQueueChart from "./DsQueueChart";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {ErrorBoundary} from "react-error-boundary";
import DsErrorFallback from "./DsErrorFallback";
import {numbersAfterAndUpToIncluding} from "./DsMonitoringUtils";


const FORMATION_TYPE_WEB = "web";
const MISSING_DATA_MIN_WIDTH_MS = 300_000;


type MeasurementAt = number[]

type Queue = {
    name: string;
    measurements: ((number)[])[];
}
type Records = {
    active: MeasurementAt[];
    queues: Queue[];
}

type AutoscaleRule = {
    id: number;
    up: number;
    down: number;
}

type ThresholdChange = {
    timestamp: number;
    autoscaleRule: AutoscaleRule;
}

type DynoAt = [number, number]

class MonitoringPayload {
    after: number;
    dynos: DynoAt[] | null;
    formationName: string;
    formationType: string;
    records: Records;
    thresholds: ThresholdChange[] | null;
    timeIncrement: number;
    until: number;

    static fromJson(json: {}): MonitoringPayload {
        let monitoringPayload = new MonitoringPayload();
        Object.assign(monitoringPayload, json);
        console.log(`monitoringPayload ⤵`);
        console.log(monitoringPayload);
        return monitoringPayload;
    }
}

MonitoringPayload.prototype.toString = function () {
    return `MonitoringPayload(after:${this.after}, before: ${this.before})`;
};

type MsSinceEpoch = number
type MeasuredValue = number | null

type ChartDatapoint = {
    x: MsSinceEpoch,
    y: MeasuredValue
}

class MonitoringVM {
    formationType: string;
    measurementsName: string;
    threshUp: Array<ChartDatapoint>;
    threshDown: Array<ChartDatapoint>;
    max: Array<ChartDatapoint>;
    maxP95?: Array<ChartDatapoint>;
    missing: Array<ChartDatapoint>;
    dynos: Array<ChartDatapoint>;


    constructor(formationType: string) {
        this.formationType = formationType;
        this.measurementsName = "";
        this.threshUp = [];
        this.threshDown = [];
        this.max = [];
        this.maxP95 = this.isWebFormation() ? [] : null;
        this.missing = [];
        this.dynos = [];
    }

    static fromPayload(payload: MonitoringPayload, queueName?: string): MonitoringVM {
        return monitoringPayloadToVM(payload, queueName);
    }

    isWebFormation(): boolean {
        return this.formationType === FORMATION_TYPE_WEB;
    }
}

/**
 * Return MonitoringVM created from MonitoringPayload
 * if queu
 * @param payload MonitoringPayload to be used as source
 * @param queueName (optional) name of queue to show, default to showing "active" measurements (this could be different
 *                  queues at different times as active rules change throughout the time )
 */
function monitoringPayloadToVM(
    payload: MonitoringPayload,
    queueName?: string
): MonitoringVM {
    console.log(`monitoringPayloadToVM ⤵ : ${payload}`);
    console.log(payload);

    const doShowActive = !queueName || queueName === ACTIVE_MEASUREMENT;
    console.log(`\t We will ${doShowActive ? "show active measurements." : `be showing queue: "${queueName}"`}`);

    const {after, until, timeIncrement, records, thresholds, dynos, formationName, formationType} = payload;
    const measurements = doShowActive ? records.active : records.queues[queueName];
    const timeIncrementMs = timeIncrement * 1_000;
    const afterMs = 1_000 * (after + timeIncrement)
    const untilMs = 1_000 * until
    const lastIndex = measurements.length - 1;

    // Build an array of all valid timestamp in the time windows of this payload

    const timeWindowMs = Array.from(
        {length: 1 + (untilMs - afterMs) / timeIncrementMs},
        (_, i) => untilMs - (i * timeIncrementMs)
    );
    timeWindowMs.reverse();

    const viewModel = new MonitoringVM(formationType);
    viewModel.measurementsName = queueName;

    function pushMeasurementDataGap(gapStart: number, gapEnd: number) {
        viewModel.max.push({x: gapStart, y: null});
        viewModel.max.push({x: gapEnd, y: null});
        if (viewModel.isWebFormation()) {
            viewModel.maxP95.push({x: gapStart, y: null});
            viewModel.maxP95.push({x: gapEnd, y: null});
        }
        viewModel.missing.push({x: gapStart, y: 1})
        viewModel.missing.push({x: gapEnd, y: 0})
    }

    for (let i = 0; i <= lastIndex; i++) {
        const measurementAt = measurements[i];
        const timestampMs = measurementAt[0] * 1_000;

        if (i === 0 && timestampMs > afterMs) {
            // missing data at start, add null to correct the chart timeline
            console.log("measurements: null at the beginning ");
            viewModel.max.push({x: afterMs, y: null});
            if (viewModel.isWebFormation()) {
                viewModel.maxP95.push({x: afterMs, y: null});
            }
            if (timestampMs - afterMs > MISSING_DATA_MIN_WIDTH_MS) {
                // null chart data between wide gaps and highlight them
                console.log("measurements: data gap at start");
                const gapEnd = timestampMs - timeIncrementMs;

                viewModel.max.push({x: gapEnd, y: null});
                if (viewModel.isWebFormation()) {
                    viewModel.maxP95.push({x: gapEnd, y: null});
                }
                // also add this gap to missing data
                viewModel.missing.push({x: afterMs, y: 1})
                viewModel.missing.push({x: gapEnd, y: 0})
            }

        }
        // null chart data between wide gaps and highlight them
        if (i > 0 && timestampMs - viewModel.max.slice(-1)[0].x > MISSING_DATA_MIN_WIDTH_MS) {
            // the gap between last datapoint and this one is too large
            console.log("measurements: data gap within");
            pushMeasurementDataGap(viewModel.max.slice(-1)[0].x + timeIncrementMs, timestampMs - timeIncrementMs);
        }

        // add the actual measurement data
        viewModel.max.push({x: timestampMs, y: measurementAt[1]});
        if (viewModel.isWebFormation()) {
            viewModel.maxP95.push({x: timestampMs, y: measurementAt[2]});
        }

        if (i === lastIndex && timestampMs < untilMs) {
            // missing data at the end, add null to correct the chart timeline
            // null chart data between last valid data and end if gap wide enough and show the gap
            console.log(`measurements: Last index: ${i}, timestampMs: ${timestampMs} viewModel.max.slice(-1)[0].x: ${viewModel.max.slice(-1)[0].x}`);
            if (untilMs - viewModel.max.slice(-1)[0].x >= MISSING_DATA_MIN_WIDTH_MS) {
                console.log("measurements: gap at the end too large ");
                // the gap between last datapoint and this one is too large
                pushMeasurementDataGap(viewModel.max.slice(-1)[0].x + timeIncrementMs, untilMs);
            }
            console.log("measurements: null at the end ");
            viewModel.max.push({x: untilMs, y: null});
            if (viewModel.isWebFormation()) {
                viewModel.maxP95.push({x: untilMs, y: null});
            }
        }
    }


    // ScaleUP/DOWN annotations ####################################################################################
    if (thresholds) {
        // Take the sparse thresholds received in payload and create a datapoint for each valid timestamp after first
        // received threshold; repeating the values until we hit next in in the sparse array from payload
        const thresholdsForShifting = [...thresholds];
        // First shallow clone the array and sort it by timestamp (just in case they didn't come sorted)
        thresholdsForShifting.sort((a, b) => {
            return a.timestamp - b.timestamp
        });
        let nextThresholdAt = thresholdsForShifting.shift();
        // let currentAutoscaleRule = thresholdsForShifting && thresholdsForShifting[0] && thresholdsForShifting[0].autoscaleRule
        let currentAutoscaleRule = null;
        for (let eachTimestamp of numbersAfterAndUpToIncluding(after, until, timeIncrement)) {
            while ( // this is a while (not if) because we should be able to skip over ALL invalidated timestamps in one go
            nextThresholdAt !== undefined
            && eachTimestamp >= nextThresholdAt.timestamp // current timestamp is at or after next threshold timestamp
            && thresholdsForShifting.length > 0 // and we have another threshold to shift to
                ) {
                // next is current
                currentAutoscaleRule = nextThresholdAt.autoscaleRule;
                nextThresholdAt = thresholdsForShifting.shift();
            }
            if (!currentAutoscaleRule) {
                // this protects filling up the start of chart with assumed data
                continue;
            }
            viewModel.threshUp.push({x: 1000 * eachTimestamp, y: currentAutoscaleRule.up});
            viewModel.threshDown.push({x: 1000 * eachTimestamp, y: currentAutoscaleRule.down});
        }
    }

    // Dynos #######################################################################################################
    if (dynos) {
        const dynosLastIndex = dynos.length - 1;
        for (let i = 0; i < dynos.length; i++) {
            const dynoAt: DynoAt = dynos[i];
            const timestampMs = 1000 * dynoAt[0];

            // if we're adding first dyno record and the timestamp is larger than payload.after add an empty record
            // because there is at least one missing dyno record and we want both charts to start at same time
            if (i === 0 && timestampMs > afterMs) {
                // if we are missing dynodata at the beginning
                console.log("dynos: null at the beginning ");
                viewModel.dynos.push({x: afterMs, y: null});
            }

            viewModel.dynos.push({x: timestampMs, y: dynoAt[1]});

            // After adding the last dyno record check if the last record timestamp equals valid window timestamp, if not
            // add empty record as we are missing data at the end of the time window
            if (i === dynosLastIndex && timestampMs < untilMs) {
                // if we are missing data at the end
                console.log("dynos: null at the end ");
                viewModel.dynos.push({x: untilMs, y: null});
            }
        }
    }

    // Return view model
    return viewModel;
}

function getQueueChartDatasets(vm: MonitoringVM): Array<Object> {
    const chartData = []
    if (vm === undefined || vm === null) {
        return chartData;
    }
    chartData.push({
        label: "Upscale Threshold",
        data: vm.threshUp,
        parsing: false,
        stepped: 'before',
        indexAxis: 'x',
        pointRadius: 0,
        fill: false,
        backgroundColor: "rgb(255, 99, 132)",
        borderColor: "rgb(255, 99, 132)",
        borderWidth: 1.5,
        borderDash: [4, 2],
    });
    chartData.push({
        label: "Downscale Threshold",
        data: vm.threshDown,
        parsing: false,
        stepped: 'before',
        indexAxis: 'x',
        pointRadius: 0,
        fill: false,
        backgroundColor: "rgb(94, 97, 223)",
        borderColor: "rgb(94, 97, 223)",
        borderWidth: 1.5,
        borderDash: [4, 2],
    });
    if (vm.isWebFormation()) {
        chartData.push({
            label: "Queue Time (P95)",
            data: vm.maxP95,
            parsing: false,
            indexAxis: 'x',
            pointRadius: 0,
            fill: false,
            backgroundColor: "rgb(53,162,235)",
            borderColor: "rgb(53,162,235)",
            borderWidth: 1,
            // stepped: 'before',
        });
        chartData.push({
            label: "Queue Time",
            data: vm.max,
            parsing: false,
            indexAxis: 'x',
            pointRadius: 0,
            // backgroundColor: "rgba(53, 162, 235, 0.4)",
            borderColor: "rgba(53,162,235,0.25)",
            borderWidth: 1,
            // stepped: 'before',
        });
    } else {
        chartData.push({
            label: "Queue Time",
            data: vm.max,
            parsing: false,
            indexAxis: 'x',
            pointRadius: 0,
            fill: true,
            backgroundColor: "rgba(53, 162, 235, 0.6)",
            borderColor: "rgb(53,162,235)",
            borderWidth: 1,
            stepped: 'before',
        });
    }
    chartData.push({
        label: "Missing Data",
        data: vm.missing,
        parsing: false,
        indexAxis: 'x',
        pointRadius: 0,
        backgroundColor: "rgba(255, 0, 0, 0.05)",
        borderWidth: 0,
        fill: true,
        stepped: true,
        yAxisID: 'y2',
    });
    return chartData;
}

function getDynoChartDatasets(data: MonitoringVM): Array<Object> {
    const chartData = []
    if (data === undefined || data === null) {
        return chartData;
    }

    chartData.push({
        label: "Dyno Count",
        data: data.dynos,
        parsing: false,
        indexAxis: 'x',
        pointRadius: 0,
        // backgroundColor: "rgba(53, 162, 235, 0.4)",
        borderColor: "rgb(123, 123, 123)",
        borderWidth: 2,
        // borderDash: [5, 10],
        stepped: "before"
    });
    return chartData;
}


const ACTIVE_MEASUREMENT = "_DS_ACTIVE_MEASUREMENT";

type DsMonitoringProps = {
    chartData: string;
    selectedQueue: string;
};

const DsMonitoring = (
    {
        chartData,
        selectedQueue,
    }: DsMonitoringProps
): JSX.Element => {

    const refQueueChart = useRef(null);
    const refDynoChart = useRef(null);

    const [payload, setPayload] = useState<MonitoringPayload>()
    const [queues, setQueues] = useState([]);
    const [measurement, setMeasurement] = useState(selectedQueue ?? ACTIVE_MEASUREMENT);
    const [monitoringVM, setMonitoringVM] = useState<MonitoringVM | undefined>(new MonitoringVM("web"));

    const [chartDatasets, setChartDatasets] = useState({
        queue: null,
        dynos: null,
    });

    useEffect(() => {
        console.log(`### DSM #### useEffect - (RE)RENDER START`);
    });


    useEffect(() => {
        console.log(`### DSM #### useEffect[chartData]`);
        setPayload(MonitoringPayload.fromJson(chartData));
    }, [chartData]);

    useEffect(() => {
        console.log(`### DSM #### useEffect[payload, measurement]\t payload: ${payload}, measurement: ${measurement}`);
        if (payload) {
            setMonitoringVM(MonitoringVM.fromPayload(payload, measurement));
            setQueues(Object.keys(payload.records?.queues));
        }
    }, [payload, measurement]);

    useEffect(() => {
        console.log(`### DSM #### useEffect[monitoringVM]`);
        // Whenever monitoringVM changes, regenerate data for charts
        const datasets = {
            queue: getQueueChartDatasets(monitoringVM),
            dynos: getDynoChartDatasets(monitoringVM),
        };
        console.log(`monitoringVM ⤵`);
        console.log(monitoringVM);
        console.log(`\tdatasets ⤵`);
        console.log(datasets);

        setChartDatasets(datasets);
    }, [monitoringVM]);

    useEffect(() => {
        console.log(`### DSM #### useEffect - (RE)RENDER END`);
    });

    //<editor-fold desc="Event Handlers">
    const handleSelectedQueueChange = (event) => {
        setMeasurement(event.target.value);
    };

    const handleOnZoomStart = useCallback(chart => {
    }, [chartData]);

    const handleOnQueueZoom = useCallback(chart => {
        /*
                const min = chart.chart.scales.x.min;
                const max = chart.chart.scales.x.max;
                setDynoZoom(min, max);
        */
    }, [chartData]);

    const handleOnDynosZoom = useCallback(chart => {
        /*
                const min = chart.chart.scales.x.min;
                const max = chart.chart.scales.x.max;
                setQueueZoom(min, max);
        */
    }, [chartData]);

    const handleOnQueueZoomComplete = useCallback(chart => {
        const min = chart.chart.scales.x.min;
        const max = chart.chart.scales.x.max;
        setDynoZoom(min, max);
    }, [chartData]);

    const handleOnDynosZoomComplete = useCallback(chart => {
        const min = chart.chart.scales.x.min;
        const max = chart.chart.scales.x.max;
        setQueueZoom(min, max);
    }, [chartData]);
    //</editor-fold>

    //<editor-fold desc="Zoom functions">

    const resetZoom = () => {
        refQueueChart.current.resetZoom();
        // Only have to reset one chart, the other will sync
        // refDynoChart.current.resetZoom(); // Only need to set
    };

    const setZoom = (min, max) => {
        refQueueChart.current.setZoom(min, max);
        // Only have to set one chart, the other will sync
        //refDynoChart.current.setZoom(min, max);
    };

    const setQueueZoom = (min, max) => {
        refQueueChart.current.setZoom(min, max);
    };

    const setDynoZoom = (min, max) => {
        refDynoChart.current.setZoom(min, max);
    };
    //</editor-fold>

    //<editor-fold desc="JSX Template return">
    return <React.Fragment>
        <div className="row justify-content-end">
            {queues.length > 0 &&
                <div className="col-md-3 form-floating">
                    <select className="form-select"
                            aria-label="Default select example"
                            defaultValue={selectedQueue}
                            onChange={handleSelectedQueueChange}>
                        <option key={ACTIVE_MEASUREMENT} value={ACTIVE_MEASUREMENT}>active</option>
                        {queues.map(queue => (<option key={queue} value={queue}>{queue}</option>))}
                    </select>
                    <label htmlFor="Selected_Queues_selected_queues">
                        <span className="fw-bolder">Selected Queues</span>
                    </label>
                </div>
            }
            <div className="col-auto">
                <div className="chart-tools ms-auto">
                    <button className="btn btn-light" onClick={resetZoom}>
                        <i data-feather="x-circle"></i>&nbsp;Reset Zoom
                    </button>
                </div>
            </div>

        </div>
        {/*QUEUE*/}
        <div className="row">
            <h2 className="text-capitalize">
                {!monitoringVM.isWebFormation() && `${measurement == ACTIVE_MEASUREMENT ? "Active" : measurement} `}
                Queue Time (ms)</h2>
        </div>
        <div className="row" data-tour="queue-time">
            <ErrorBoundary FallbackComponent={DsErrorFallback}>
                <DsQueueChart
                    ref={refQueueChart}
                    onZoomStart={handleOnZoomStart}
                    onZoom={handleOnQueueZoom}
                    onZoomComplete={handleOnQueueZoomComplete}
                    datasets={chartDatasets.queue}
                />
            </ErrorBoundary>
        </div>
        <hr/>
        {/*DYNOS*/}
        <div className="row">
            <h2 className="text-capitalize">{payload?.formationName} Dyno Count</h2>
        </div>
        <div className="row" data-tour="dyno-count">
            <ErrorBoundary FallbackComponent={DsErrorFallback}>
                <DsDynoChart
                    ref={refDynoChart}
                    onZoomStart={handleOnZoomStart}
                    onZoom={handleOnDynosZoom}
                    onZoomComplete={handleOnDynosZoomComplete}
                    datasets={chartDatasets && chartDatasets.dynos}
                />
            </ErrorBoundary>
        </div>
    </React.Fragment>
    //</editor-fold>
}

export default DsMonitoring
